// ============================================================================
// ADD MANUAL CONTENT DIALOG - Add manual content to the knowledge base
// ============================================================================

import { useState } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { knowledgeApi, type KnowledgeSource, type KnowledgeCategory } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

// ============================================================================
// CONSTANTS
// ============================================================================

const CATEGORIES: { value: KnowledgeCategory; label: string; icon: string }[] = [
  { value: 'framework', label: 'Framework', icon: '🧩' },
  { value: 'case_study', label: 'Case Study', icon: '📖' },
  { value: 'best_practice', label: 'Best Practice', icon: '✅' },
  { value: 'template', label: 'Template', icon: '📋' },
  { value: 'research', label: 'Research', icon: '🔬' },
  { value: 'guide', label: 'Guide', icon: '📘' },
  { value: 'other', label: 'Other', icon: '📄' },
]

// ============================================================================
// COMPONENT
// ============================================================================

interface AddManualContentDialogProps {
  onClose: () => void
  onSourceCreated: (source: KnowledgeSource) => void
}

export function AddManualContentDialog({ onClose, onSourceCreated }: AddManualContentDialogProps) {
  const { accountId } = useSessionStore()
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<KnowledgeCategory>('other')
  const [tags, setTags] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!accountId) {
      setError('No account selected')
      return
    }

    if (!title.trim()) {
      setError('Title is required')
      return
    }

    if (!content.trim() || content.trim().length < 10) {
      setError('Content must be at least 10 characters')
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      const result = await knowledgeApi.addManual(accountId, {
        title: title.trim(),
        content: content.trim(),
        description: description.trim() || undefined,
        category,
        tags: tags
          .split(',')
          .map(t => t.trim())
          .filter(Boolean),
      })

      onSourceCreated(result.source)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add content')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Manual Content</DialogTitle>
          <DialogDescription>
            Add your own content to the knowledge base. Use this for internal best practices,
            frameworks, or any custom knowledge you want the AI to reference.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Our JTBD Interview Process"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={category} onValueChange={(val) => setCategory(val as KnowledgeCategory)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Content */}
            <div className="space-y-2">
              <Label htmlFor="content">Content *</Label>
              <Textarea
                id="content"
                placeholder="Enter your knowledge content here. You can use markdown formatting."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={10}
                className="font-mono text-sm"
              />
              <p className="text-xs text-zinc-500">
                {content.length} characters {content.length < 10 && '(minimum 10 required)'}
              </p>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="Brief description of what this content covers"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label htmlFor="tags">Tags (optional)</Label>
              <Input
                id="tags"
                placeholder="product-management, strategy, internal"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
              />
              <p className="text-xs text-zinc-500">Separate tags with commas</p>
            </div>

            {/* Error */}
            {error && (
              <p className="text-sm text-red-500">{error}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Adding...' : 'Add Content'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddManualContentDialog
